import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Tabs from '~/components/Tabs'

const TabsPage: Component = () => {
  const [activeTab, setActiveTab] = createSignal('overview')

  const businessTabs = [
    {
      id: 'overview',
      label: 'Vista General',
      icon: 'material-symbols-light:bar-chart',
      title: 'Panel Ejecutivo',
      subtitle: 'Monitoreo de KPIs y métricas críticas en tiempo real',
      content: (
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div class="p-6 bg-linear-to-br from-primary-500 to-primary-700 rounded-3xl text-white shadow-xl shadow-primary-500/20">
            <div class="flex items-center justify-between mb-4">
              <Icon icon="material-symbols-light:arrow-upward" size={32} class="text-white/50" />
              <Badge color="success" size="sm" variant="soft" class="bg-white/20 text-white italic">
                Live
              </Badge>
            </div>
            <div class="text-3xl font-black mb-1">$48,290.00</div>
            <div class="text-xs text-white/70 uppercase tracking-widest font-bold">Ingresos Mensuales</div>
          </div>
          <div class="p-6 bg-surface rounded-3xl border border-border shadow-sm">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 rounded-2xl bg-surface-muted flex items-center justify-center">
                <Icon icon="material-symbols-light:group-outline" class="text-secondary-500" size={24} />
              </div>
              <div>
                <div class="text-2xl font-bold text-foreground line-clamp-1">12,402</div>
                <div class="text-[10px] text-foreground-subtle font-bold uppercase">Usuarios Activos</div>
              </div>
            </div>
            <div class="h-1.5 w-full bg-surface-muted rounded-full overflow-hidden">
              <div class="h-full bg-secondary-500 w-3/4 rounded-full"></div>
            </div>
          </div>
          <div class="p-6 bg-surface rounded-3xl border border-border shadow-sm">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 rounded-2xl bg-success-soft flex items-center justify-center">
                <Icon icon="material-symbols-light:language" class="text-emerald-500" size={24} />
              </div>
              <div>
                <div class="text-2xl font-bold text-foreground line-clamp-1">99.9%</div>
                <div class="text-[10px] text-foreground-subtle font-bold uppercase">Uptime Global</div>
              </div>
            </div>
            <div class="flex gap-1">
              {[1, 2, 3, 4, 5, 6, 7].map(() => (
                <div class="h-4 w-full bg-emerald-500/20 rounded-sm"></div>
              ))}
              <div class="h-4 w-full bg-emerald-500 rounded-sm animate-pulse"></div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'settings',
      label: 'Configuración',
      icon: 'material-symbols-light:settings-outline',
      title: 'Preferencias del Sistema',
      subtitle: 'Administra los parámetros globales de tu instancia',
      content: (
        <div class="p-10 bg-surface-muted rounded-3xl border border-border border-dashed text-center animate-in zoom-in-95 duration-500">
          <Icon icon="material-symbols-light:settings-outline" size={64} class="mx-auto text-slate-300 mb-4" />
          <h4 class="text-lg font-bold text-foreground">Panel de Control en Construcción</h4>
          <p class="text-sm text-foreground-subtle max-w-sm mx-auto mt-2">Estamos optimizando los motores de configuración para ofrecerte una experiencia más fluida.</p>
        </div>
      )
    }
  ]

  return (
    <>
      <Title>Tabs - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Tabs</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Navegación por contextos. El componente Tabs organiza la información en paneles lógicamente separados, optimizando el espacio y permitiendo transiciones fluidas entre diferentes vistas
            operativas.
          </p>
        </div>

        <div class="space-y-12">
          {/* Casos de Uso Empresarial */}
          <Card>
            <Card.Header
              title="Navegación Estructurada"
              subtitle="Implementación de pestañas con soporte para iconos, badges y metadatos informativos."
              icon={<Icon icon="material-symbols-light:widgets-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10">
                <Tabs items={businessTabs} activeTab={activeTab()} onChange={setActiveTab} variant="underline" transition="fade" />
              </div>
            </Card.Body>
          </Card>

          {/* Variaciones de Estilo */}
          <Card>
            <Card.Header
              title="Matriz de Estilos"
              subtitle="Cuatro variantes visuales para adaptarse a la densidad de información de tu UI."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-12 p-8 bg-surface-muted rounded-3xl border border-border">
                <div class="space-y-8">
                  <div class="space-y-3">
                    <Badge variant="soft" color="primary" size="sm">
                      Default (Border Bottom)
                    </Badge>
                    <Tabs
                      items={[
                        { id: '1', label: 'Drafts' },
                        { id: '2', label: 'Sent' }
                      ]}
                      activeTab="1"
                      showContent={false}
                    />
                  </div>
                  <div class="space-y-3">
                    <Badge variant="soft" color="secondary" size="sm">
                      Pills (Inverted BG)
                    </Badge>
                    <Tabs
                      items={[
                        { id: '1', label: 'Active', badge: 12 },
                        { id: '2', label: 'Archived' }
                      ]}
                      activeTab="1"
                      variant="pills"
                      showContent={false}
                    />
                  </div>
                </div>
                <div class="space-y-8">
                  <div class="space-y-3">
                    <Badge variant="soft" color="info" size="sm">
                      Underline (Dynamic Offset)
                    </Badge>
                    <Tabs
                      items={[
                        { id: '1', label: 'Profile', icon: 'material-symbols-light:person-outline' },
                        { id: '2', label: 'Security' }
                      ]}
                      activeTab="1"
                      variant="underline"
                      showContent={false}
                    />
                  </div>
                  <div class="space-y-3">
                    <Badge variant="soft" color="warning" size="sm">
                      Buttons (Grouped Controls)
                    </Badge>
                    <Tabs
                      items={[
                        { id: '1', label: 'List' },
                        { id: '2', label: 'Grid' }
                      ]}
                      activeTab="1"
                      variant="buttons"
                      showContent={false}
                    />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Tabs."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">items</td>
                      <td class="px-6 py-4 italic text-xs">TabItemType[]</td>
                      <td class="px-6 py-4 font-mono">[]</td>
                      <td class="px-6 py-4 italic">Lista de objetos {`{ id, label, icon, badge, title, subtitle, content }`}.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">variant</td>
                      <td class="px-6 py-4 italic text-xs">'default' | 'pills' | 'underline' | 'buttons'</td>
                      <td class="px-6 py-4 font-mono">'default'</td>
                      <td class="px-6 py-4">Apariencia visual del header.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">transition</td>
                      <td class="px-6 py-4 italic text-xs">'fade' | 'slide' | 'none'</td>
                      <td class="px-6 py-4 font-mono">'fade'</td>
                      <td class="px-6 py-4 italic">Efecto de animación entre paneles de contenido.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">fullWidth</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Expande los tabs para llenar el contenedor horizontalmente.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default TabsPage
